// Shoot scheduler for one production (the Production calendar opens it from a Production page ticket,
// location.state.openSchedulerFor). Migrated in place (DESIGN rev 2.1): only the calendar uses it.
// Same calls: GET /api/shoot-segments/by-project/:id, GET /api/talent (when the page passed no list),
// POST /api/shoot-segments/:id (add), PATCH /api/shoot-segments/:segId, DELETE /api/shoot-segments/:segId,
// GET /api/shoot-segments/availability-check, GET + PATCH /api/live-production/:id, POST /api/shoot-segments/:id/archive.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Archive, Plus, Trash2, TriangleAlert, X } from 'lucide-react';
import api from '../lib/api';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import DatePicker from '../ui/DatePicker';
import EmptyState from '../ui/EmptyState';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Skeleton from '../ui/Skeleton';
import StageChip from '../ui/StageChip';
import Tag from '../ui/Tag';
import Textarea from '../ui/Textarea';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { stageOf } from '../ui/stages';
import { clean } from '../shell/copy';
import Modal from '../ui/Modal';

export default function SchedulerPanel({ record, allTalent = [], onClose, onProjectUpdated }) {
  const confirm = useConfirm();
  const toast = useToast();
  const [segments,    setSegments]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [notes,       setNotes]       = useState(record.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [archiving,   setArchiving]   = useState(false);
  const [savedFlash,  setSavedFlash]  = useState(false);

  // Fields save on blur / change; Save flushes the focused field (forces its blur) and confirms visibly.
  const handleSave = useCallback(() => {
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) document.activeElement.blur();
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  }, []);

  const [talentList, setTalentList] = useState(allTalent);

  const loadSegments = useCallback(async () => {
    try {
      const r = await api.get(`/api/shoot-segments/by-project/${record.id}`);
      setSegments(r.data);
    } catch { /* the empty state stands */ } finally { setLoading(false); }
  }, [record.id]);

  useEffect(() => { loadSegments(); }, [loadSegments]);
  useEffect(() => {
    if (talentList.length === 0) api.get('/api/talent').then(r => setTalentList(r.data)).catch(() => {});
  }, [talentList.length]);

  const saveNotes = useCallback(async () => {
    if (notes === (record.notes || '')) return;
    setSavingNotes(true);
    try {
      const r = await api.patch(`/api/live-production/${record.id}`, { notes });
      onProjectUpdated?.(r.data);
    } catch {
      toast('The notes did not save. Try again.', { tone: 'danger' });
    } finally { setSavingNotes(false); }
  }, [notes, record.id, record.notes, onProjectUpdated, toast]);

  const addSegment = useCallback(async () => {
    const label = `Cam ${segments.length + 1}`;
    try {
      const r = await api.post(`/api/shoot-segments/${record.id}`, { segmentLabel: label, sortOrder: segments.length });
      setSegments(prev => [...prev, r.data]);
    } catch {
      toast('The segment was not added. Try again.', { tone: 'danger' });
    }
  }, [segments.length, record.id, toast]);

  const patchSegment = useCallback(async (segmentId, patch) => {
    setSegments(prev => prev.map(s => (s.id === segmentId ? { ...s, ...patch, talents: patch.talents ? patch.talents : s.talents } : s)));
    try {
      const r = await api.patch(`/api/shoot-segments/${segmentId}`, patch);
      setSegments(prev => prev.map(s => (s.id === segmentId ? r.data : s)));
      // A filmed / incomplete flip may move the production's status: refresh it
      if (patch.shootStatus !== undefined) {
        try {
          const p = await api.get(`/api/live-production/${record.id}`);
          onProjectUpdated?.(p.data);
        } catch { /* the calendar refreshes on close */ }
      }
    } catch {
      toast('The segment did not update. Try again.', { tone: 'danger' });
      loadSegments();
    }
  }, [record.id, loadSegments, onProjectUpdated, toast]);

  const deleteSegment = useCallback(async (segment) => {
    const ok = await confirm({
      title: 'Delete this segment?',
      body: `Delete ${clean(segment.segmentLabel) || 'this segment'} from the shoot plan. It leaves the calendar too.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    setSegments(prev => prev.filter(s => s.id !== segment.id));
    try {
      await api.delete(`/api/shoot-segments/${segment.id}`);
      try {
        const p = await api.get(`/api/live-production/${record.id}`);
        onProjectUpdated?.(p.data);
      } catch { /* the calendar refreshes on close */ }
    } catch {
      toast('The segment was not deleted. Try again.', { tone: 'danger' });
      loadSegments();
    }
  }, [record.id, loadSegments, onProjectUpdated, confirm, toast]);

  const archiveProject = useCallback(async () => {
    const ok = await confirm({
      title: 'Archive this production?',
      body: 'Every segment is filmed. Archiving takes the production off the active shoot schedule.',
      confirmLabel: 'Archive',
    });
    if (!ok) return;
    setArchiving(true);
    try {
      await api.post(`/api/shoot-segments/${record.id}/archive`, { archived: true });
      onProjectUpdated?.({ ...record, isSchedulerArchived: true });
      onClose();
    } catch {
      toast('The production was not archived. Try again.', { tone: 'danger' });
    } finally { setArchiving(false); }
  }, [record, onProjectUpdated, onClose, confirm, toast]);

  const filmedCount = useMemo(() => segments.filter(s => s.shootStatus === 'filmed').length, [segments]);
  const allFilmed   = segments.length > 0 && filmedCount === segments.length;
  const title = clean(record.script?.scriptName || record.notes || 'Untitled production');
  const stage = stageOf({ cardStatus: record.status });

  return (
    <Modal size="wide" title={record.episodeNumber ? `${title}, Ep ${record.episodeNumber}` : title} onClose={onClose}
      meta={`Shoot scheduler. ${segments.length === 0 ? 'No segments yet' : `${filmedCount} of ${segments.length} filmed`}`}
      footer={(
        <>
          {savedFlash && <span className="ui-meta" role="status">Saved</span>}
          <Button variant="quiet" onClick={onClose}>Close</Button>
          <Button variant="primary" onClick={handleSave}>Save</Button>
        </>
      )}>
      <div className="ui-cluster">
        <StageChip stage={stage} />
        {record.status === 'Incomplete' && <Badge condition="Incomplete" />}
        {record.client?.name && <Tag>{clean(record.client.name)}</Tag>}
      </div>

      <section className="ui-section" aria-labelledby="sp-segments">
        <div className="ui-section__head ui-section__head--center">
          <h3 className="ui-section__title" id="sp-segments">Shoot segments</h3>
          <div className="ui-section__actions"><Button variant="secondary" icon={Plus} onClick={addSegment}>Add segment</Button></div>
        </div>
        {loading ? (
          <div className="ui-stack"><Skeleton width="60%" /><Skeleton width="45%" /></div>
        ) : segments.length === 0 ? (
          <div className="ui-region"><EmptyState message="No segments yet. Add one to start planning the shoot setups." /></div>
        ) : (
          <div className="ui-stack">
            {segments.map(seg => (
              <SegmentRow key={seg.id} segment={seg} talentList={talentList}
                onChange={patch => patchSegment(seg.id, patch)} onDelete={() => deleteSegment(seg)} />
            ))}
          </div>
        )}
      </section>

      <Textarea label={savingNotes ? 'Notes for filming (saving)' : 'Notes for filming'} hint="Production notes, the same field as on the Production page."
        value={notes} onChange={e => setNotes(e.target.value)} onBlur={saveNotes} rows={3} />

      {allFilmed && !record.isSchedulerArchived && (
        <div className="ui-region ui-region--pad ui-stack">
          <p className="ui-p">Every segment is filmed. Take this production off the active shoot schedule?</p>
          <span><Button variant="secondary" icon={Archive} onClick={archiveProject} loading={archiving}>Archive production</Button></span>
        </div>
      )}
    </Modal>
  );
}

function SegmentRow({ segment, talentList, onChange, onDelete }) {
  const [label,   setLabel]   = useState(segment.segmentLabel);
  const [setting, setSetting] = useState(segment.setting || '');
  useEffect(() => { setLabel(segment.segmentLabel); }, [segment.segmentLabel]);
  useEffect(() => { setSetting(segment.setting || ''); }, [segment.setting]);

  const shootDayStr = segment.shootDay ? segment.shootDay.slice(0, 10) : '';
  const isFilmed    = segment.shootStatus === 'filmed';

  // Talent assigned AND a day set: check each one against that day's availability.
  const [availWarnings, setAvailWarnings] = useState({});
  useEffect(() => {
    if (!shootDayStr) { setAvailWarnings({}); return undefined; }
    const assigned = (segment.talents || []).filter(t => t.talentId);
    if (assigned.length === 0) { setAvailWarnings({}); return undefined; }
    let cancelled = false;
    (async () => {
      const out = {};
      await Promise.all(assigned.map(async (t) => {
        try {
          const r = await api.get(`/api/shoot-segments/availability-check?talentId=${t.talentId}&date=${shootDayStr}`);
          if (!r.data.available) out[t.talentId] = true;
        } catch { /* no warning */ }
      }));
      if (!cancelled) setAvailWarnings(out);
    })();
    return () => { cancelled = true; };
  }, [shootDayStr, segment.talents]);

  const toPayload = list => list.map(t => ({ talentId: t.talentId || null, talentDisplayName: t.talentDisplayName || null }));
  const addTalent = (talentId) => {
    if (!talentId) return;
    if ((segment.talents || []).some(t => t.talentId === talentId)) return;
    onChange({ talents: toPayload([...(segment.talents || []), { talentId }]) });
  };
  const removeTalent = (idx) => onChange({ talents: toPayload((segment.talents || []).filter((_, i) => i !== idx)) });
  const name = clean(segment.segmentLabel) || 'this segment';

  return (
    <div className="ui-region ui-region--pad ui-stack">
      <div className="ui-cluster">
        <Checkbox label="Filmed" checked={isFilmed} onChange={() => onChange({ shootStatus: isFilmed ? 'incomplete' : 'filmed' })} />
        <Input aria-label="Segment label" className="ui-grow" value={label} placeholder="Segment label, for example Cam 1"
          onChange={e => setLabel(e.target.value)} onBlur={() => { if (label !== segment.segmentLabel) onChange({ segmentLabel: label }); }} />
        <Button variant="quiet" iconOnly icon={Trash2} label={`Delete ${name}`} onClick={onDelete} />
      </div>
      <div className="ui-cluster">
        {(segment.talents || []).map((t, idx) => {
          const who = clean(t.talent?.name || t.talentDisplayName || 'Unknown talent');
          return (
            <span key={t.id || idx} className="ui-chip ui-chip--static" data-tone={availWarnings[t.talentId] ? 'danger' : undefined}
              title={availWarnings[t.talentId] ? `${who} is not available on this day` : who}>
              <span className="ui-chip__main">
                {availWarnings[t.talentId] && <Icon icon={TriangleAlert} label="Not available on this day" />}
                <span className="ui-chip__title">{who}</span>
              </span>
              <button type="button" className="ui-chip__x" aria-label={`Remove ${who}`} onClick={() => removeTalent(idx)}><Icon icon={X} /></button>
            </span>
          );
        })}
        <Select aria-label={`Add talent to ${name}`} value="" placeholder="Add talent"
          onChange={e => addTalent(e.target.value)}
          options={talentList.filter(t => !(segment.talents || []).some(st => st.talentId === t.id)).map(t => ({ value: t.id, label: clean(t.name) }))} />
      </div>
      <div className="ui-cluster">
        <DatePicker aria-label={`Shoot day for ${name}`} value={shootDayStr} onChange={e => onChange({ shootDay: e.target.value || null })} />
        <Input aria-label={`Setting for ${name}`} className="ui-grow" value={setting} placeholder="Setting, for example the news desk"
          onChange={e => setSetting(e.target.value)} onBlur={() => { if (setting !== (segment.setting || '')) onChange({ setting }); }} />
      </div>
    </div>
  );
}
